import React, { useState, useEffect } from 'react';
import { FileText, Plus, Pencil, Trash, Mail } from 'lucide-react';
import http from '../../config/http';
import DrawerWrapper from '../DrawerWrapper';
import { CustomTable } from '../Table/CustomTable';

export default function TemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    text: '',
    module: 'customer'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await http.get('/api/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTemplate) {
        await http.put(`/api/templates/${formData._id}`, formData);
        setTemplates(prev => prev.map(t => (t._id === formData._id ? formData : t)));
      } else {
        const { data } = await http.post('/api/templates', formData);
        setTemplates(prev => [...prev, data]);
      }

      setDrawerOpen(false);
      setEditingTemplate(null);
      setFormData({ name: '', subject: '', text: '', module: 'customer' });
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await http.delete(`/api/templates/${id}`);
        setTemplates(prev => prev.filter(t => t._id !== id));
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Failed to delete template');
      }
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      ...template
    });
    setDrawerOpen(true);
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  };

  const getModuleBadge = (module) => {
    const moduleColors = {
      'customer': 'bg-blue-100 text-blue-800',
      'event': 'bg-green-100 text-green-800',
      'invoice': 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${moduleColors[module] || 'bg-gray-100 text-gray-800'}`}>
        {module ? module.charAt(0).toUpperCase() + module.slice(1) : 'Unknown'}
      </span>
    );
  };

  // Define columns for CustomTable
  const columns = [
    {
      header: "Template",
      accessorKey: "name",
      cell: ({ row }) => {
        const template = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-sm font-semibold">
              {getInitials(template.name)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{template.name}</div>
            </div>
          </div>
        );
      }
    },
    {
      header: "Subject",
      accessorKey: "subject",
      cell: ({ row }) => (
        <div>
          <div className="text-sm text-gray-900">{row.original.subject}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
            <Mail className="w-3 h-3" />
            Email subject
          </div>
        </div>
      )
    },
    {
      header: "Module",
      accessorKey: "module",
      cell: ({ row }) => getModuleBadge(row.original.module),
      meta: {
        filterVariant: "select",
        defaultLabel: "All Modules"
      }
    },
    {
      header: "Preview",
      accessorKey: "text",
      cell: ({ row }) => (
        <div>
          <div className="text-sm text-gray-600 line-clamp-2">
            {row.original.text}
          </div>
        </div>
      )
    },
    {
      header: "Action",
      isHideSort: true,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEditTemplate(row.original)}
            className="py-2 px-3 border shadow rounded-lg flex items-center gap-2"
            title="Edit Template"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => deleteTemplate(row.original._id)}
            className="py-2 px-3 border shadow rounded-lg flex items-center gap-2"
            title="Delete Template"
          >
            <Trash size={14} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="mb-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Message Templates</h2>
          <p className="text-gray-600 mt-1">Manage your email and message templates</p>
        </div>

        {/* Stats Cards */}
        <div className="flex items-center gap-4 flex-wrap mb-6">
          {/* Total Templates Card */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-gray-900">{templates.length}</div>
            <div className="text-sm text-gray-600">Total Templates</div>
          </div>
          
          {/* Customer Templates */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-blue-600">{templates.filter(t => t.module === 'customer').length}</div>
            <div className="text-sm text-gray-600">Customer</div>
          </div>
          
          {/* Event Templates */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-green-600">{templates.filter(t => t.module === 'event').length}</div>
            <div className="text-sm text-gray-600">Event</div>
          </div>
          
          {/* Invoice Templates */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-purple-600">{templates.filter(t => t.module === 'invoice').length}</div>
            <div className="text-sm text-gray-600">Invoice</div>
          </div>
        </div>
      </div>

      {/* Main Content Card with CustomTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <CustomTable
          data={templates}
          columns={columns}
          dataNotFoundQuery="No templates yet. Get started by adding your first message template."
          additionalActions={
            <button
              onClick={() => {
                setEditingTemplate(null);
                setFormData({ 
                  name: '', 
                  subject: '', 
                  text: '',
                  module: 'customer'
                });
                setDrawerOpen(true);
              }}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-medium text-sm shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Add Template
            </button>
          }
        />
      </div>

      {/* Enhanced Drawer */}
      <DrawerWrapper
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingTemplate(null);
        }}
        title={editingTemplate ? 'Edit Template' : 'Add New Template'}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Template Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="e.g., Welcome Email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Email subject line"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Module *</label>
                <select
                  value={formData.module}
                  onChange={e => setFormData({ ...formData, module: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  <option value="customer">Customer</option>
                  <option value="event">Event</option>
                  <option value="invoice">Invoice</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message text *</label>
                <textarea
                  value={formData.text}
                  onChange={e => setFormData({ ...formData, text: e.target.value })}
                  rows={10}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Template content..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can use variables like {'{customer_name}'}, {'{event_date}'}, etc.
                </p>
              </div>
            </div>
          </div>

          {/* Delete Section (only for editing) */}
          {editingTemplate && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-4">Permanently delete this template and all associated data.</p>
              <button
                type="button"
                onClick={() => deleteTemplate(editingTemplate._id)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Delete Template
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="border-t border-gray-200 pt-6 flex justify-between">
            <button
              type="button"
              onClick={() => {
                setDrawerOpen(false);
                setEditingTemplate(null);
              }}
              className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium text-sm transition-colors"
            >
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      </DrawerWrapper>
    </div>
  );
}
